/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 사용자 데이터 저장 store
 * 버전 : 1.0
 * 파일명 : StorageStore.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정일 : 2025-12-26, 강동형 : 전반적인 로직 리펙토링
 * ================================================================ 
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

type StorageType = "local" | "session";

interface StorageState {
  storageType: StorageType;
  setStorageType: (t: StorageType) => void;

  data: Record<string, any>;
  getStorage: () => Storage;

  getItem: <T = any>(key: string) => T | null;
  setItem: (key: string, value: any) => void;
  removeItem: (key: string) => void;
  clear: () => void;

  hydrateKey: (key: string) => void;
}

const safeParse = (raw: string | null) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const useStorageStore = create<StorageState>()(
  persist(
    (set, get) => ({
      storageType: "local",

      setStorageType: (t) => set({ storageType: t }),

      data: {},

      getStorage: () => (get().storageType === "local" ? localStorage : sessionStorage),

      getItem: (key) => {
        // ✅ 캐시에 있으면 캐시 우선
        if (key in get().data) return get().data[key] ?? null;

        const storage = get().getStorage();
        const parsed = safeParse(storage.getItem(key));

        // ✅ 읽은 김에 캐시에 적재
        set((s) => ({ data: { ...s.data, [key]: parsed } }));
        return parsed;
      },

      hydrateKey: (key) => {
        const storage = get().getStorage();
        const parsed = safeParse(storage.getItem(key));
        set((s) => ({ data: { ...s.data, [key]: parsed } }));
      },

      setItem: (key, value) => {
        const storage = get().getStorage();
        storage.setItem(key, JSON.stringify(value));

        // ✅ 핵심: 메모리 캐시 갱신 → 구독 컴포넌트 리렌더
        set((s) => ({ data: { ...s.data, [key]: value } }));
      },

      removeItem: (key) => {
        const storage = get().getStorage();
        storage.removeItem(key);
        set((s) => {
          const next = { ...s.data };
          delete next[key];
          return { data: next };
        });
      },

      clear: () => {
        const storage = get().getStorage();
        storage.clear();
        set({ data: {} });
      },
    }),
    {
      name: "storage_store", // ✅ persist는 '스토어 상태' 저장(여기선 storageType, data)
      // (원하면 data는 persist에서 제외하도록 partialize 가능)
      partialize: (state) => ({ storageType: state.storageType }), // ✅ 보통은 이것만 저장 추천
    }
  )
);
